import { useEffect, useMemo, useRef, useState } from 'react';
import { CloudUpload, FileUp, Folder } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import type { PickedFile } from '@/lib/fileTree';
import {
  useUploadEngFileVersion,
  useUploadEngFiles,
  type EngFile,
  type EngFolder,
} from '@/features/engineering/hooks';
import { MAX_ENG_FILE_BYTES, formatBytes } from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { FileIcon } from './EngParts';
import { folderPathLabel } from './engFileUtils';
import { categoryForFileName } from '@/features/engineering/model';

// ---------------------------------------------------------------------------
// Batch upload (files, folders, drops)
// ---------------------------------------------------------------------------

export interface PendingUpload {
  folderId: string | null;
  entries: PickedFile[];
}

export function EngUploadDialog({
  pending,
  onClose,
  projectId,
  files,
  folders,
}: {
  pending: PendingUpload | null;
  onClose: () => void;
  projectId: string;
  files: EngFile[];
  folders: EngFolder[];
}) {
  const upload = useUploadEngFiles();
  const [summary, setSummary] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    if (!pending) {
      setSummary('');
      setProgress(null);
    }
  }, [pending]);

  const analysis = useMemo(() => {
    if (!pending) return { rows: [], tooBig: 0, versioned: 0, created: 0 };
    const key = (folderId: string | null, name: string) => `${folderId ?? 'root'}/${name.toLowerCase()}`;
    const existingByKey = new Map<string, EngFile>();
    for (const f of files) existingByKey.set(key(f.folder_id, f.name), f);
    // Folder ids are only known for dirs that already exist; new dirs can't
    // contain existing files, so a miss there is correctly "new file".
    const folderKey = (parentId: string | null, name: string) => `${parentId ?? 'root'}/${name.toLowerCase()}`;
    const folderIds = new Map<string, string>();
    for (const f of folders) folderIds.set(folderKey(f.parent_id, f.name), f.id);
    const resolveFolder = (dirs: string[]): string | null | undefined => {
      let cur: string | null = pending.folderId;
      for (const dir of dirs) {
        const id = folderIds.get(folderKey(cur, dir));
        if (!id) return undefined;
        cur = id;
      }
      return cur;
    };
    let tooBig = 0;
    let versioned = 0;
    let created = 0;
    const rows = pending.entries.map((entry) => {
      const big = entry.file.size > MAX_ENG_FILE_BYTES;
      if (big) tooBig += 1;
      const folderId = resolveFolder(entry.dirs);
      const match = folderId === undefined ? undefined : existingByKey.get(key(folderId, entry.file.name));
      if (!big) {
        if (match) versioned += 1;
        else created += 1;
      }
      return { entry, big, match };
    });
    return { rows, tooBig, versioned, created };
  }, [pending, files, folders]);

  const submit = async () => {
    if (!pending) return;
    const entries = analysis.rows.filter((r) => !r.big).map((r) => r.entry);
    if (entries.length === 0) {
      toast.error('Nothing to upload — every file is over the 500 MB limit.');
      return;
    }
    try {
      const result = await upload.mutateAsync({
        projectId,
        folderId: pending.folderId,
        entries,
        existingFiles: files,
        existingFolders: folders,
        changeSummary: summary.trim() || null,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const parts: string[] = [];
      if (result.created) parts.push(`${result.created} new file${result.created === 1 ? '' : 's'}`);
      if (result.versioned) parts.push(`${result.versioned} new version${result.versioned === 1 ? '' : 's'}`);
      if (result.failed.length === 0) {
        toast.success(`Uploaded ${parts.join(' and ') || 'files'}.`);
      } else {
        toast.warning(
          `${parts.join(' and ') || 'Nothing'} uploaded; ${result.failed.length} failed: ${result.failed
            .slice(0, 3)
            .map((f) => `${f.name} (${f.error})`)
            .join('; ')}${result.failed.length > 3 ? '…' : ''}`,
        );
      }
      onClose();
    } catch (err) {
      toast.error(formatError(err, 'Upload failed.'));
    }
  };

  const total = pending?.entries.length ?? 0;
  const totalBytes = pending?.entries.reduce((s, e) => s + e.file.size, 0) ?? 0;

  return (
    <Dialog open={!!pending} onOpenChange={(open) => !open && !upload.isPending && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CloudUpload className="h-4 w-4" />
            Upload {total} {total === 1 ? 'file' : 'files'}
          </DialogTitle>
          <DialogDescription>
            Into <span className="font-medium text-foreground">{folderPathLabel(pending?.folderId ?? null, folders)}</span>
            {' · '}
            {formatBytes(totalBytes)}
            {analysis.versioned > 0 && (
              <>
                {' · '}
                <span className="text-foreground">
                  {analysis.versioned} will become new version{analysis.versioned === 1 ? '' : 's'} of existing files
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
          <ul className="divide-y divide-border text-sm">
            {analysis.rows.map(({ entry, big, match }, i) => (
              <li key={`${entry.dirs.join('/')}/${entry.file.name}-${i}`} className="flex items-center gap-2 px-3 py-1.5">
                <FileIcon category={categoryForFileName(entry.file.name)} className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">
                  {entry.dirs.length > 0 && (
                    <span className="text-muted-foreground">
                      <Folder className="mr-0.5 inline h-3 w-3" />
                      {entry.dirs.join('/')}/
                    </span>
                  )}
                  {entry.file.name}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(entry.file.size)}</span>
                {big ? (
                  <Badge variant="warning-soft" className="shrink-0 text-[10px]">
                    Over 500 MB
                  </Badge>
                ) : match ? (
                  <Badge variant="info-soft" className="shrink-0 text-[10px]">
                    v{match.version_count + 1}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    New
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="eng-upload-summary">What changed? {analysis.versioned > 0 ? '' : '(optional)'}</Label>
          <Textarea
            id="eng-upload-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={
              analysis.versioned > 0
                ? 'e.g. Rim thickened to 3 mm, added draft angle on the base'
                : 'Shown in the version history of every file in this batch'
            }
            className="min-h-[64px]"
            disabled={upload.isPending}
          />
        </div>

        {progress && upload.isPending && (
          <div className="space-y-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-info transition-all"
                style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}…
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={upload.isPending || total === 0}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// New version of one file (check-in)
// ---------------------------------------------------------------------------

export function EngNewVersionDialog({
  file,
  onClose,
  checkedOutByMe,
}: {
  file: EngFile | null;
  onClose: () => void;
  checkedOutByMe: boolean;
}) {
  const upload = useUploadEngFileVersion();
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<File | null>(null);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    if (!file) {
      setPicked(null);
      setSummary('');
    }
  }, [file]);

  const nameMismatch = picked && file && picked.name.toLowerCase() !== file.name.toLowerCase();

  const submit = async () => {
    if (!file || !picked) {
      toast.error('Choose the updated file first.');
      return;
    }
    if (picked.size > MAX_ENG_FILE_BYTES) {
      toast.error('That file is over the 500 MB limit.');
      return;
    }
    if (!summary.trim()) {
      toast.error('Add a short summary of what changed — future you will thank you.');
      return;
    }
    try {
      const version = await upload.mutateAsync({ file, upload: picked, changeSummary: summary });
      toast.success(`${file.name} is now v${version.version_no}.`);
      onClose();
    } catch (err) {
      toast.error(formatError(err, 'Could not upload the new version.'));
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(open) => !open && !upload.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-4 w-4" />
            {checkedOutByMe ? 'Check in' : 'Upload new version'}
          </DialogTitle>
          <DialogDescription>
            {file && (
              <>
                <span className="font-medium text-foreground">{file.name}</span> is at v{file.version_count}. The upload
                becomes v{file.version_count + 1}; every earlier version stays downloadable.
                {checkedOutByMe && ' Your check-out is released automatically.'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              e.target.value = '';
              if (f) setPicked(f);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center gap-3 rounded-lg border border-dashed border-border px-4 py-4 text-left text-sm transition-colors hover:border-info/60 hover:bg-info-soft/10"
          >
            <FileUp className="h-5 w-5 shrink-0 text-info" />
            {picked ? (
              <span className="min-w-0">
                <span className="block truncate font-medium text-foreground">{picked.name}</span>
                <span className="block text-xs text-muted-foreground">{formatBytes(picked.size)} · click to choose a different file</span>
              </span>
            ) : (
              <span className="text-muted-foreground">Choose the edited file from your computer…</span>
            )}
          </button>
          {nameMismatch && (
            <p className="rounded-md border border-fresh-salmon/50 bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
              The file name differs from <span className="font-medium">{file?.name}</span>. That’s fine — it will still be
              stored as the next version of this file — but double-check you picked the right one.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="eng-version-summary">What changed?</Label>
            <Textarea
              id="eng-version-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g. Increased wall thickness to 2.4 mm; fixed the snap-fit tolerance"
              className="min-h-[80px]"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={upload.isPending || !picked}>
            {upload.isPending ? 'Uploading…' : checkedOutByMe ? 'Check in' : 'Upload version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
